import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  createChallengeService,
  createChallenge,
  getChallengesTable,
  getChallengesTablePaginated,
  getChallengesByOrganization,
  addOrganizationsToChallenge,
  previewMergeChallenges,
  mergeChallenges,
  getChallengeTemplates,
} from '../services/challenge.service';
import { ChallengeDto } from '../types/dtos/challenge';
import { CustomResponseEntity } from '../types/common';
import {
  CreateChallengeDto,
  ChallengeTableResponse,
  ChallengesTableParams,
  PaginatedChallengeTableResponse,
  ChallengesByOrganizationResponse,
  AddOrgsResultDto,
  MergePreviewRequestDto,
  MergePreviewResponseDto,
  MergeRequestDto,
  MergeResultDto,
  ChallengeTemplateDto,
} from '../types/dtos/challenge/ChallengeDto';

export const useChallenges = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery<CustomResponseEntity<ChallengeDto[]>, Error>({
    queryKey: ['challenges', selectedOrganization?.id],
    queryFn: async () => {
      if (!selectedOrganization?.id) {
        throw new Error('Organization ID is required');
      }
      const service = createChallengeService(selectedOrganization.id);
      return service.findAll();
    },
    enabled: !!selectedOrganization?.id,
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
};

/**
 * Hook for fetching challenges by organization ID (prop-based, not context-based)
 * Use this in admin routes or components where OrganizationContext is not available
 *
 * @param organizationId - The organization ID to fetch challenges for
 * @returns Challenges data for the specified organization
 */
export const useChallengesByOrgId = (organizationId: string) => {
  const shouldRun = !!organizationId && organizationId !== 'placeholder';

  return useQuery<CustomResponseEntity<ChallengeDto[]>, Error>({
    queryKey: ['challenges', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      try {
        const service = createChallengeService(organizationId);
        return await service.findAll();
      } catch (error: any) {
        // For 403 and 404 errors, return empty data instead of throwing
        // This prevents React Query from marking it as an error and triggering infinite refetches
        if (error?.response?.status === 403 || error?.status === 403 ||
            error?.response?.status === 404 || error?.status === 404) {
          return {
            data: [],
            status: 'OK',
            code: 200,
            httpErrorCode: 0,
            description: 'No challenges found'
          } as CustomResponseEntity<ChallengeDto[]>;
        }
        throw error;
      }
    },
    enabled: shouldRun,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useCreateChallenge = () => {
  const queryClient = useQueryClient();
  return useMutation<CustomResponseEntity<any>, Error, CreateChallengeDto>({
    mutationFn: createChallenge as any,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
};

/**
 * Hook for adding organizations to an existing challenge.
 * Copies challenge configuration and creates new Inspection entities for each org.
 */
export const useAddOrganizationsToChallenge = () => {
  const queryClient = useQueryClient();
  return useMutation<
    CustomResponseEntity<AddOrgsResultDto>,
    Error,
    { challengeId: string; inspectedOrgIds: string[] }
  >({
    mutationFn: ({ challengeId, inspectedOrgIds }) =>
      addOrganizationsToChallenge(challengeId, inspectedOrgIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      queryClient.invalidateQueries({ queryKey: ['challengesTablePaginated'] });
    },
  });
};

/**
 * Hook for fetching challenges table data (non-paginated - legacy)
 * @deprecated Use useChallengesTablePaginated instead for better performance
 */
export const useChallengesTable = (organizationId: string, options?: { enabled?: boolean }) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!organizationId;
  // Don't run query if organizationId is placeholder or empty
  const shouldRun = isEnabled && !!organizationId && organizationId !== 'placeholder';
  
  return useQuery({
    queryKey: ['challengesTable', organizationId],
    queryFn: async () => {
      try {
        return await getChallengesTable(organizationId);
      } catch (error: any) {
        // For 404 errors, return empty data structure instead of throwing
        // This prevents React Query from marking it as an error and triggering refetches
        if (error?.response?.status === 404 || error?.status === 404) {
          return {
            data: {
              challenges: [],
              programVersionItems: []
            }
          } as unknown as ChallengeTableResponse;
        }
        // Re-throw other errors
        throw error;
      }
    },
    enabled: shouldRun,
    staleTime: Infinity, // Never consider data stale - prevents automatic refetches
    gcTime: 10 * 60 * 1000, // 10 minutes - cache retention time
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false, // Disable automatic polling/refetching
    retry: false, // Disable retries completely - 404 is expected when no data exists yet
    refetchOnMount: false, // Don't refetch when component remounts
  });
};

/**
 * Hook for fetching paginated challenges table data with search support
 * 
 * @param organizationId - Parent organization ID
 * @param params - Pagination and search parameters
 * @param options - Query options (enabled)
 * @returns Paginated challenge table data
 */
export const useChallengesTablePaginated = (
  organizationId: string,
  params: ChallengesTableParams = {},
  options?: { enabled?: boolean }
) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!organizationId;
  const shouldRun = isEnabled && !!organizationId && organizationId !== 'placeholder';
  
  return useQuery<PaginatedChallengeTableResponse, Error>({
    queryKey: ['challengesTablePaginated', organizationId, params],
    queryFn: async () => {
      try {
        return await getChallengesTablePaginated(organizationId, params);
      } catch (error: any) {
        // For 404 errors, return empty data structure instead of throwing
        if (error?.response?.status === 404 || error?.status === 404) {
          return {
            data: {
              challenges: [],
              programVersionItems: []
            },
            totalElements: 0,
            totalPages: 0,
            page: params.page || 0,
            size: params.size || 50,
            message: 'No data',
            type: 'OK'
          } as PaginatedChallengeTableResponse;
        }
        throw error;
      }
    },
    enabled: shouldRun,
    staleTime: 30000, // 30 seconds - reasonable for paginated data
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook for fetching challenge data for a specific organization (for FarmDetailsModal)
 * Fetches only the challenge data for the specified inspected organization
 * 
 * @param parentOrgId - Parent organization ID
 * @param inspectedOrgId - The organization/farm to get challenge data for
 * @param options - Query options (enabled)
 * @returns Challenge data for the specific organization
 */
export const useChallengesByOrganization = (
  parentOrgId: string,
  inspectedOrgId: string,
  options?: { enabled?: boolean }
) => {
  const isEnabled = options?.enabled !== undefined 
    ? options.enabled 
    : !!parentOrgId && !!inspectedOrgId;
  const shouldRun = isEnabled && 
    !!parentOrgId && 
    parentOrgId !== 'placeholder' &&
    !!inspectedOrgId &&
    inspectedOrgId !== 'placeholder';
  
  return useQuery<ChallengesByOrganizationResponse, Error>({
    queryKey: ['challengesByOrganization', parentOrgId, inspectedOrgId],
    queryFn: async () => {
      try {
        return await getChallengesByOrganization(parentOrgId, inspectedOrgId);
      } catch (error: any) {
        // For 404 errors, return empty data structure instead of throwing
        if (error?.response?.status === 404 || error?.status === 404) {
          return {
            data: {
              challenges: [],
              programVersionItems: []
            },
            message: 'No data',
            type: 'OK'
          } as ChallengesByOrganizationResponse;
        }
        throw error;
      }
    },
    enabled: shouldRun,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook for previewing a challenge merge.
 */
export const usePreviewMergeChallenges = () => {
  return useMutation<
    CustomResponseEntity<MergePreviewResponseDto>,
    Error,
    MergePreviewRequestDto
  >({
    mutationFn: previewMergeChallenges,
  });
};

/**
 * Hook for executing a challenge merge.
 * Invalidates challenges queries on success so the UI refreshes.
 */
export const useMergeChallenges = () => {
  const queryClient = useQueryClient();
  return useMutation<CustomResponseEntity<MergeResultDto>, Error, MergeRequestDto>({
    mutationFn: mergeChallenges,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      queryClient.invalidateQueries({ queryKey: ['challengesTablePaginated'] });
    },
  });
};

/**
 * Hook for fetching challenge templates (grouped by name with org counts).
 * Uses the lightweight /templates endpoint instead of loading full inspection data.
 */
export const useChallengeTemplates = () => {
  const { selectedOrganization } = useOrganization();

  return useQuery<CustomResponseEntity<ChallengeTemplateDto[]>, Error>({
    queryKey: ['challengeTemplates', selectedOrganization?.id],
    queryFn: () => getChallengeTemplates(),
    enabled: !!selectedOrganization?.id,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
};